import { type ReactElement } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { Loading } from '@/components/shared/placeholders/Loading';

import { ShadowAiToolsPage } from './ShadowAiToolsPage';
import { ShadowAiNetworkPage } from './ShadowAiNetworkPage';

/**
 * Shadow AI section. Rendered at `/:organizationId/shadow-ai/*` and split into
 * two sidebar sub-pages:
 *   - index (`/shadow-ai`)                → Tools (counters + Browser/Installed/Running tabs)
 *   - `/shadow-ai/network-discovery`      → Network Discovery (counters + traffic table)
 *
 * Network Discovery is gated by the `SHADOW_AI_NETWORK_DISCOVERY` flag; when off,
 * its route redirects back to Tools so a stale deep link never renders blank.
 */
export default function OrgShadowAITab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { flags, isLoading } = useFeatureFlags([
    flagsmithFeatureFlags.SHADOW_AI_NETWORK_DISCOVERY,
  ]);
  const isNetworkDiscoveryEnabled =
    flags[flagsmithFeatureFlags.SHADOW_AI_NETWORK_DISCOVERY] ?? false;

  if (!organizationId) {
    return null;
  }

  const toolsPath = `/${organizationId}/shadow-ai`;

  return (
    <Routes>
      <Route
        index
        element={<ShadowAiToolsPage organizationId={organizationId} />}
      />
      <Route
        path="network-discovery"
        element={
          isLoading ? (
            // Don't redirect while the flag is still resolving — a valid deep
            // link would otherwise be replaced away before the real value
            // arrives, and the user would stay on Tools even once it enables.
            <Loading label="Loading Network Discovery..." />
          ) : isNetworkDiscoveryEnabled ? (
            <ShadowAiNetworkPage organizationId={organizationId} />
          ) : (
            <Navigate to={toolsPath} replace />
          )
        }
      />
      <Route path="*" element={<Navigate to={toolsPath} replace />} />
    </Routes>
  );
}
